/**
 * Bridgeman Collections - Main JavaScript
 * Complete Scroll System v3.0
 * 
 * SINGLE scroll system using GSAP ScrollToPlugin
 * NO CSS scroll-snap, NO conflicting handlers
 */

// ============================================
// GLOBAL STATE
// ============================================
const State = {
  INTRO: 'intro',
  READY: 'ready',
  SCROLLABLE: 'scrollable'
};

let currentState = State.INTRO;
let currentSectionIndex = 0;
let isScrolling = false;
let sections = [];
let scrollHandlersInitialized = false;

// ============================================
// INITIALIZATION
// ============================================
document.addEventListener('DOMContentLoaded', () => {
  console.log('🎬 Bridgeman Collections v3.0');
  
  if (typeof gsap === 'undefined') {
    console.error('❌ GSAP not loaded');
    document.body.style.overflow = '';
    return;
  }
  
  gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);
  
  // Check if we're on homepage (has film-intro) or other pages
  const isHomepage = document.getElementById('film-intro') !== null;
  
  if (!isHomepage) {
    // Normal pages: just enable regular scroll + reveal-on-scroll.
    console.log('📄 Regular page - normal scroll enabled');
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';

    // Reveal [data-animate] elements via IntersectionObserver (adds .is-visible).
    const animated = document.querySelectorAll('[data-animate]');
    if (animated.length) {
      const io = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        }
      }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });
      animated.forEach(el => io.observe(el));
    }

    initParallaxEffects();
    initCustomCursor();
    initMagneticButtons();
    return;
  }
  
  // Cache ALL sections including film-intro (only on homepage)
  sections = Array.from(document.querySelectorAll('.section[data-section]'));
  console.log(`📍 Found ${sections.length} sections:`, sections.map(s => s.dataset.section));

  const isHome = document.body.classList.contains('template-index');

  if (isHome) {
    // Homepage: snap-scroll systeem (wheel preventDefault + section-by-section).
    // Scroll unlocked — intro speelt visueel parallel via film-intro.js.
    unlockScroll();
    startScrollSystem();
  } else {
    // Non-home: NATIVE scroll. Alleen zachte effecten (parallax, animaties) zonder
    // wheel te hijacken.
    unlockScroll();
    currentState = State.SCROLLABLE;
    initSectionAnimations();
    initParallax();
    initMagneticButtons();
    initCustomCursor();
    ScrollTrigger.refresh();
  }
});

// ============================================
// PARALLAX EFFECTS (for all pages)
// ============================================
function initParallaxEffects() {
  // Simple parallax for background images
  const parallaxBgs = document.querySelectorAll('[data-parallax-strength]');
  parallaxBgs.forEach(bg => {
    const strength = parseFloat(bg.dataset.parallaxStrength) || 10;
    gsap.to(bg, {
      yPercent: strength,
      ease: 'none',
      scrollTrigger: {
        trigger: bg.closest('.section') || bg,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true
      }
    });
  });
}

// ============================================
// SCROLL LOCK
// ============================================
function lockScroll() {
  document.body.style.overflow = 'hidden';
  document.documentElement.style.overflow = 'hidden';
  document.body.style.position = 'fixed';
  document.body.style.width = '100%';
  document.body.style.top = '0';
}

function unlockScroll() {
  document.body.style.overflow = '';
  document.documentElement.style.overflow = '';
  document.body.style.position = '';
  document.body.style.width = '';
  document.body.style.top = '';
}

// ============================================
// INTRO ANIMATION
// ============================================
function initIntro() {
  const intro = document.getElementById('film-intro');
  const scrollHint = document.getElementById('scroll-hint');
  
  // Hide scroll hint initially
  if (scrollHint) {
    gsap.set(scrollHint, { opacity: 0, visibility: 'hidden' });
  }
  
  if (!intro) {
    console.log('⚠️ No intro found');
    // Show scroll hint immediately if no intro
    if (scrollHint) {
      gsap.to(scrollHint, { opacity: 1, visibility: 'visible', duration: 0.5 });
    }
    startScrollSystem();
    return;
  }
  
  // Check if using new scene-based system (handled by film-intro.js)
  if (intro.dataset.totalScenes) {
    console.log('🎬 Using scene-based Film Intro system');
    // Listen for completion event from FilmIntroController
    intro.addEventListener('introComplete', () => {
      console.log('✅ Film Intro complete (from controller)');
      currentState = State.READY;
      waitForScroll();
    });
    return;
  }
  
  // Legacy intro animation (single scene)
  const bgDelay = parseFloat(intro.dataset.imageRevealDelay) || 3.5;
  const bgDuration = parseFloat(intro.dataset.imageRevealDuration) || 1.2;
  
  // Elements
  const letters = intro.querySelectorAll('.film-intro__letter');
  const tagline = intro.querySelector('.film-intro__tagline');
  const line = intro.querySelector('.film-intro__line');
  const subtitle = intro.querySelector('.film-intro__subtitle');
  const bgImage = intro.querySelector('.section__bg-image');
  
  // Timeline
  const tl = gsap.timeline({
    onComplete: () => {
      console.log('✅ Intro complete');
      currentState = State.READY;
      waitForScroll();
    }
  });
  
  // Letters one by one
  letters.forEach((letter, i) => {
    tl.to(letter, {
      opacity: 1,
      y: 0,
      duration: 0.1,
      ease: 'power2.out'
    }, 0.4 + (i * 0.05));
  });
  
  // Tagline
  if (tagline) {
    tl.to(tagline, { opacity: 1, duration: 0.5 }, 1.2);
  }
  
  // Line
  if (line) {
    tl.to(line, { scaleX: 1, duration: 0.6 }, 1.6);
  }
  
  // Subtitle
  if (subtitle) {
    tl.to(subtitle, { opacity: 1, duration: 0.4 }, 2.0);
  }
  
  // Background image fade
  if (bgImage) {
    tl.to(bgImage, {
      opacity: 1,
      duration: bgDuration,
      ease: 'power2.inOut'
    }, bgDelay);
  }
  
  // Show scroll hint at end of intro (floating, stays visible)
  if (scrollHint) {
    tl.to(scrollHint, { 
      opacity: 1, 
      visibility: 'visible', 
      duration: 0.5 
    }, bgDelay + bgDuration + 0.2);
  }
}

// ============================================
// WAIT FOR FIRST SCROLL
// ============================================
function waitForScroll() {
  let handled = false;
  
  const onFirstScroll = (e) => {
    if (handled) return;
    handled = true;
    
    // Prevent jump
    if (e.cancelable) {
      e.preventDefault();
    }
    
    // Cleanup
    window.removeEventListener('wheel', onFirstScroll, true);
    window.removeEventListener('touchstart', onFirstScroll, true);
    window.removeEventListener('keydown', onFirstScroll, true);
    
    console.log('🚀 First scroll detected');
    startScrollSystem();
  };
  
  window.addEventListener('wheel', onFirstScroll, { passive: false, capture: true });
  window.addEventListener('touchstart', onFirstScroll, { passive: false, capture: true });
  window.addEventListener('keydown', onFirstScroll, { capture: true });
}

// ============================================
// START SCROLL SYSTEM
// ============================================
function startScrollSystem() {
  console.log('🎥 Starting scroll system...');
  
  // Unlock scroll
  unlockScroll();
  
  // Reset to top
  window.scrollTo(0, 0);
  
  // Update state
  currentState = State.SCROLLABLE;
  currentSectionIndex = 0;
  
  // Initialize modules
  initScrollHandlers();
  initDotNav();
  initSectionAnimations();
  initParallax();
  initKenBurns();
  initMagneticButtons();
  initCustomCursor();
  
  // Set first section active
  if (sections[0]) {
    sections[0].classList.add('is-active');
    updateDot(sections[0].dataset.section);
  }
  
  // Refresh ScrollTrigger
  ScrollTrigger.refresh();
  
  console.log('✅ Scroll system ready');
}

// ============================================
// SCROLL HANDLERS - SINGLE SOURCE OF TRUTH
// ============================================
function initScrollHandlers() {
  if (scrollHandlersInitialized) return;
  scrollHandlersInitialized = true;
  
  let lastScrollTime = 0;
  let accumulatedDelta = 0;
  const DEBOUNCE = 800;
  const DELTA_THRESHOLD = 50;
  
  // WHEEL - MUST prevent default to stop native scroll
  window.addEventListener('wheel', (e) => {
    // Always prevent native scroll
    e.preventDefault();
    
    if (currentState !== State.SCROLLABLE) return;
    if (isScrolling) return;
    
    const now = Date.now();
    
    // Reset accumulated delta after debounce period
    if (now - lastScrollTime > DEBOUNCE) {
      accumulatedDelta = 0;
    }
    
    accumulatedDelta += e.deltaY;
    
    // Only scroll when accumulated delta exceeds threshold
    if (Math.abs(accumulatedDelta) < DELTA_THRESHOLD) return;
    
    // Check debounce
    if (now - lastScrollTime < DEBOUNCE) return;
    lastScrollTime = now;
    
    const dir = accumulatedDelta > 0 ? 1 : -1;
    accumulatedDelta = 0;
    
    goToSection(currentSectionIndex + dir);
  }, { passive: false });
  
  // TOUCH
  let touchY = 0;
  
  window.addEventListener('touchstart', (e) => {
    if (currentState !== State.SCROLLABLE) return;
    touchY = e.touches[0].clientY;
  }, { passive: true });
  
  window.addEventListener('touchmove', (e) => {
    // Prevent native scroll on touch
    if (currentState === State.SCROLLABLE) {
      e.preventDefault();
    }
  }, { passive: false });
  
  window.addEventListener('touchend', (e) => {
    if (currentState !== State.SCROLLABLE) return;
    if (isScrolling) return;
    
    const diff = touchY - e.changedTouches[0].clientY;
    if (Math.abs(diff) < 50) return;
    
    const dir = diff > 0 ? 1 : -1;
    goToSection(currentSectionIndex + dir);
  }, { passive: true });
  
  // KEYBOARD
  window.addEventListener('keydown', (e) => {
    if (currentState !== State.SCROLLABLE) return;
    if (isScrolling) return;
    
    let dir = 0;
    if (e.key === 'ArrowDown' || e.key === 'PageDown' || e.key === ' ') dir = 1;
    if (e.key === 'ArrowUp' || e.key === 'PageUp') dir = -1;
    
    if (dir !== 0) {
      e.preventDefault();
      goToSection(currentSectionIndex + dir);
    }
  });
}

// ============================================
// GO TO SECTION
// ============================================
function goToSection(index) {
  if (index < 0 || index >= sections.length) return;
  if (isScrolling) return;
  
  isScrolling = true;
  currentSectionIndex = index;
  
  const target = sections[index];
  console.log(`📍 Going to: ${target.dataset.section}`);
  
  gsap.to(window, {
    duration: 0.8,
    scrollTo: {
      y: target,
      autoKill: false
    },
    ease: 'power2.inOut',
    onComplete: () => {
      isScrolling = false;
      target.classList.add('is-active');
      updateDot(target.dataset.section);
    }
  });
}

// ============================================
// DOT NAVIGATION
// ============================================
function initDotNav() {
  document.querySelectorAll('.dot-nav__item').forEach(dot => {
    dot.addEventListener('click', () => {
      if (isScrolling) return;
      
      const name = dot.dataset.section;
      const idx = sections.findIndex(s => s.dataset.section === name);
      
      if (idx !== -1) {
        goToSection(idx);
      }
    });
  });
}

function updateDot(name) {
  document.querySelectorAll('.dot-nav__item').forEach(dot => {
    dot.classList.toggle('active', dot.dataset.section === name);
  });
}

// ============================================
// SECTION ANIMATIONS
// ============================================
function initSectionAnimations() {
  sections.forEach((section, i) => {
    if (i === 0) return;
    
    const els = section.querySelectorAll('[data-animate]');
    if (!els.length) return;
    
    gsap.fromTo(els,
      { opacity: 0, y: 30 },
      {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.1,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: section,
          start: 'top 70%',
          toggleActions: 'play none none reverse'
        }
      }
    );
  });
}

// ============================================
// PARALLAX
// ============================================
function initParallax() {
  document.querySelectorAll('.section__bg-image').forEach(bg => {
    const strength = parseFloat(bg.dataset.parallaxStrength || 0);
    if (strength < 1) return;
    
    gsap.to(bg, {
      yPercent: strength,
      ease: 'none',
      scrollTrigger: {
        trigger: bg.closest('.section'),
        start: 'top bottom',
        end: 'bottom top',
        scrub: true
      }
    });
  });
}

// ============================================
// KEN BURNS
// ============================================
function initKenBurns() {
  document.querySelectorAll('.section__bg-image').forEach(bg => {
    const scale = parseFloat(bg.dataset.kenburnsScale || 1);
    const speed = parseFloat(bg.dataset.kenburnsSpeed || 12);
    
    if (scale <= 1.01) return;
    
    gsap.to(bg, {
      scale: scale,
      duration: speed,
      ease: 'sine.inOut',
      yoyo: true,
      repeat: -1
    });
  });
}

// ============================================
// MAGNETIC BUTTONS
// ============================================
function initMagneticButtons() {
  const buttons = document.querySelectorAll('.section__cta, .dot-nav__item');
  
  buttons.forEach(btn => {
    btn.addEventListener('mouseenter', () => {
      btn.style.cursor = 'pointer';
    });
    
    btn.addEventListener('mousemove', (e) => {
      const rect = btn.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;
      
      gsap.to(btn, {
        x: x * 0.3,
        y: y * 0.3,
        duration: 0.3,
        ease: 'power2.out'
      });
    });
    
    btn.addEventListener('mouseleave', () => {
      gsap.to(btn, {
        x: 0,
        y: 0,
        duration: 0.5,
        ease: 'elastic.out(1, 0.5)'
      });
    });
  });
}

// ============================================
// CUSTOM CURSOR
// ============================================
function initCustomCursor() {
  // Create cursor element - ring only with arrow on hover
  const cursor = document.createElement('div');
  cursor.className = 'custom-cursor';
  cursor.innerHTML = '<div class="custom-cursor__ring"><span class="custom-cursor__arrow">→</span></div>';
  document.body.appendChild(cursor);
  
  const ring = cursor.querySelector('.custom-cursor__ring');
  
  // Track mouse - instant sync
  document.addEventListener('mousemove', (e) => {
    ring.style.left = e.clientX + 'px';
    ring.style.top = e.clientY + 'px';
  });
  
  // Hover effects - show arrow
  const hoverables = document.querySelectorAll('a, button, .section__cta, .dot-nav__item, [data-hover]');
  
  hoverables.forEach(el => {
    el.addEventListener('mouseenter', () => {
      cursor.classList.add('is-hover');
    });
    
    el.addEventListener('mouseleave', () => {
      cursor.classList.remove('is-hover');
    });
  });
  
  // Hide on mouse leave window
  document.addEventListener('mouseleave', () => {
    cursor.style.opacity = '0';
  });
  
  document.addEventListener('mouseenter', () => {
    cursor.style.opacity = '1';
  });
}
